import * as THREE from "three";
import type { SceneHandle } from "../scene/renderer.js";
import { instantiate, loadModel, type ModelInstance } from "../assets/loader.js";
import type { BattleEvent, BattleInput, CombatantInput, Side } from "@zend/rules";
import { COSMETICS, archetypeById, unitById } from "@zend/content";
import { audio } from "../audio/engine.js";
import { BattleFx, type BattleFxKind } from "./BattleFx.js";
import { fitBattleCamera } from "./fitBattleCamera.js";
import { isUltimate, statusFx, weaponFx } from "./presentation.js";
import { applyCosmeticPaint } from "../assets/cosmeticPaint.js";
import { FieldEnvironment } from "./FieldEnvironment.js";
import { modelForwardCorrection, targetYaw } from "./aim.js";
import { BATTLE_SLOT_POSITIONS } from "./formationLayout.js";

export interface Anchor {
  id: string;
  x: number;
  y: number;
  visible: boolean;
}

interface Actor {
  id: string;
  side: Side;
  slot: string;
  defId: string;
  group: THREE.Group;
  instance: ModelInstance | null;
  height: number;
  alive: boolean;
  hideAt: number | null;
  idleAt: number | null;
  marker: THREE.Mesh;
  loading: boolean;
  disposePaint: (() => void) | null;
  aimTarget: string | null;
  turret: THREE.Object3D | null;
  turretRest: THREE.Quaternion | null;
}

/**
 * The replay arena: both squads face each other on a terrain plate; events drive attack/hit/death clips,
 * beams between actors and the HTML anchors for HP bars and floating numbers (40-combat.md R33).
 */
export class BattleScene {
  readonly root = new THREE.Group();
  private readonly actors = new Map<string, Actor>();
  readonly fx: BattleFx;
  private readonly environment = new FieldEnvironment();
  private readonly localGeometry: THREE.BufferGeometry[] = [];
  private readonly localMaterial: THREE.Material[] = [];
  private readonly markerGeometry = new THREE.CylinderGeometry(0.45, 0.5, 0.12, 12);
  private readonly markerMaterials = [
    new THREE.MeshStandardMaterial({ color: 0x66dbc5, roughness: 0.7 }),
    new THREE.MeshStandardMaterial({ color: 0xe58a66, roughness: 0.7 }),
  ];
  private readonly floor: THREE.InstancedMesh;
  private readonly slab: THREE.Mesh;
  private readonly centerLine: THREE.Mesh;
  private readonly offFrame: () => void;
  private time = 0;
  private disposed = false;
  private extras = 0;
  private reducedMotion = false;
  private playbackSpeed = 1;
  private paused = false;
  private aspect = 0;
  private visuals: Readonly<Record<string, string | null>> = {};
  private savedCamera: {
    position: THREE.Vector3;
    target: THREE.Vector3;
    minDistance: number;
    maxDistance: number;
    minPolarAngle: number;
    maxPolarAngle: number;
  } | null = null;

  constructor(
    private readonly handle: SceneHandle,
    private readonly onModelError: () => void = () => {},
  ) {
    this.root.name = "battle";
    this.root.position.y = 12; // above the base plateau, out of the way
    handle.scene.add(this.root);
    this.fx = new BattleFx();
    this.root.add(this.fx.group);
    this.root.add(this.environment.group);
    // Quiet warm paving makes weapon colours and unit silhouettes readable at battle scale.
    const slab = new THREE.Mesh(
      new THREE.BoxGeometry(14, 0.6, 9),
      new THREE.MeshStandardMaterial({ color: 0x48564d, roughness: 0.95 }),
    );
    slab.position.y = -0.3;
    slab.receiveShadow = true;
    this.root.add(slab);
    this.slab = slab;
    this.localGeometry.push(slab.geometry);
    this.localMaterial.push(slab.material);
    const pavingGeometry = new THREE.BoxGeometry(1, 0.05, 1);
    const pavingMaterial = new THREE.MeshStandardMaterial({ color: 0xc4bd8e, roughness: 1 });
    this.localGeometry.push(pavingGeometry);
    this.localMaterial.push(pavingMaterial);
    this.floor = new THREE.InstancedMesh(pavingGeometry, pavingMaterial, 256);
    this.floor.receiveShadow = true;
    this.root.add(this.floor);
    const line = new THREE.Mesh(
      new THREE.BoxGeometry(0.06, 0.02, 8.5),
      new THREE.MeshStandardMaterial({
        color: 0xd3d2a6,
        roughness: 0.8,
      }),
    );
    line.position.y = 0.06;
    this.centerLine = line;
    this.root.add(line);
    this.localGeometry.push(line.geometry, this.markerGeometry);
    this.localMaterial.push(line.material, ...this.markerMaterials);
    this.slab.visible = this.floor.visible = this.centerLine.visible = false;
    this.offFrame = handle.onFrame((dt) => this.tick(dt));
  }

  /** Points the shared camera at the arena and remembers where it was. */
  takeCamera(): void {
    const c = this.handle.controls;
    if (!this.savedCamera)
      this.savedCamera = {
        position: this.handle.camera.position.clone(),
        target: c.target.clone(),
        minDistance: c.minDistance,
        maxDistance: c.maxDistance,
        minPolarAngle: c.minPolarAngle,
        maxPolarAngle: c.maxPolarAngle,
      };
    this.fitCamera();
  }

  private fitCamera(): void {
    const c = this.handle.controls;
    const bounds = this.formationBounds().translate(this.root.position);
    bounds.getCenter(c.target);
    c.target.y = this.root.position.y + 0.8;
    const distance = fitBattleCamera(this.handle.camera, c.target, bounds);
    c.minDistance = distance * 0.92;
    c.maxDistance = Math.max(30, distance * 1.5);
    c.minPolarAngle = 0.45;
    c.maxPolarAngle = 1.15;
    this.aspect = this.handle.camera.aspect;
    c.update();
  }

  private formationBounds(): THREE.Box3 {
    const bounds = new THREE.Box3();
    for (const actor of this.actors.values()) {
      const position = actor.group.position;
      bounds.expandByPoint(new THREE.Vector3(position.x - 1.3, 0, position.z - 1.3));
      bounds.expandByPoint(new THREE.Vector3(position.x + 1.3, 2.5, position.z + 1.3));
    }
    return bounds.isEmpty()
      ? new THREE.Box3(new THREE.Vector3(-4, 0, -3), new THREE.Vector3(4, 2.5, 3))
      : bounds;
  }

  private buildFloor(): void {
    const bounds = this.formationBounds(),
      size = bounds.getSize(new THREE.Vector3()),
      center = bounds.getCenter(new THREE.Vector3());
    const width = size.x + 1.6,
      depth = size.z + 1.6;
    this.slab.scale.set(width / 14, 0.5, depth / 9);
    this.slab.position.set(center.x, -0.18, center.z);
    const columns = Math.ceil(width),
      rows = Math.ceil(depth),
      cellX = width / columns,
      cellZ = depth / rows;
    const matrix = new THREE.Matrix4(),
      color = new THREE.Color();
    let index = 0;
    for (let x = 0; x < columns; x++)
      for (let z = 0; z < rows; z++) {
        matrix.makeScale(cellX - 0.025, 1, cellZ - 0.025);
        matrix.setPosition(
          center.x - width / 2 + (x + 0.5) * cellX,
          -0.025,
          center.z - depth / 2 + (z + 0.5) * cellZ,
        );
        this.floor.setMatrixAt(index, matrix);
        this.floor.setColorAt(index++, color.setScalar(0.91 + ((x * 13 + z * 7) % 5) * 0.018));
      }
    this.floor.count = index;
    this.floor.instanceMatrix.needsUpdate = true;
    if (this.floor.instanceColor) this.floor.instanceColor.needsUpdate = true;
    this.floor.computeBoundingSphere();
    this.centerLine.position.z = center.z;
    this.centerLine.scale.z = (depth - 0.4) / 8.5;
  }

  releaseCamera(): void {
    if (!this.savedCamera) return;
    const c = this.handle.controls;
    c.target.copy(this.savedCamera.target);
    this.handle.camera.position.copy(this.savedCamera.position);
    c.minDistance = this.savedCamera.minDistance;
    c.maxDistance = this.savedCamera.maxDistance;
    c.minPolarAngle = this.savedCamera.minPolarAngle;
    c.maxPolarAngle = this.savedCamera.maxPolarAngle;
    c.update();
    this.savedCamera = null;
  }

  private modelFor(c: CombatantInput): { id: string; variant?: number | string } {
    if (c.kind === "turret") return { id: "bld.turret_emplacement", variant: 1 };
    if (c.kind === "zombie")
      return { id: c.defId, variant: c.level >= 60 ? "mutated" : c.level >= 30 ? "decayed" : "fresh" };
    return { id: c.defId };
  }

  private position(side: Side, slot: string, index: number): THREE.Vector3 {
    const [x, z] = BATTLE_SLOT_POSITIONS[slot] ?? [1, index * 0.8];
    const sign = side === "attacker" ? -1 : 1;
    return new THREE.Vector3(x * sign, 0, z + (slot === "extra" ? (index % 3) * 1.1 - 1.1 : 0));
  }

  /** Creates the actors for a battle input; models stream in and start idling. */
  load(input: BattleInput, visuals: Readonly<Record<string, string | null>> = {}): void {
    this.clear();
    this.visuals = { ...visuals };
    const sides: { side: Side; members: CombatantInput[] }[] = [
      { side: "attacker", members: input.attacker.members },
      { side: "defender", members: input.defender.members },
    ];
    for (const s of sides) s.members.forEach((m, i) => this.addActor(m, s.side, i));
    if (input.structures)
      input.structures.turrets.forEach((t, i) =>
        this.addActor(
          {
            id: `turret_${i + 1}`,
            kind: "turret",
            defId: "turret",
            slot: "turret",
            level: 1,
            stars: 0,
            abilityLevels: [1],
            stats: {
              hp: t.hp,
              atk: t.atk,
              def: 0,
              intervalTicks: 15,
              crit: 0,
              critDmg: 1.5,
              acc: 100,
              eva: 0,
              energyPerAttack: 0,
              cargo: 0,
            },
            hpPct: 100,
          },
          "defender",
          i,
        ),
      );
    this.buildFloor();
    if (this.savedCamera) this.fitCamera();
  }

  private addActor(c: CombatantInput, side: Side, index: number): void {
    const group = new THREE.Group();
    group.name = `combatant:${c.id}`;
    const pos = this.position(side, c.slot, index);
    group.position.copy(pos);
    group.rotation.y = side === "attacker" ? Math.PI / 2 : -Math.PI / 2;
    this.root.add(group);
    const marker = new THREE.Mesh(this.markerGeometry, this.markerMaterials[side === "attacker" ? 0 : 1]);
    marker.position.y = 0.12;
    group.add(marker);
    const actor: Actor = {
      id: c.id,
      side,
      slot: c.slot,
      defId: c.defId,
      group,
      instance: null,
      height: 1.4,
      alive: true,
      hideAt: null,
      idleAt: null,
      marker,
      loading: true,
      disposePaint: null,
      aimTarget: null,
      turret: null,
      turretRest: null,
    };
    this.actors.set(c.id, actor);
    const m = this.modelFor(c);
    void loadModel(m.id, m.variant)
      .then((model) => {
        if (this.disposed || this.actors.get(c.id) !== actor) return;
        const inst = instantiate(model);
        const skin = COSMETICS.find(
          (cosmetic) =>
            cosmetic.id === this.visuals[c.id] &&
            cosmetic.kind === "unit_skin" &&
            cosmetic.target === c.defId,
        );
        if (skin)
          actor.disposePaint = applyCosmeticPaint(inst.object, {
            id: skin.id,
            kind: skin.kind,
            colour: skin.colour ?? null,
          });
        const box = new THREE.Box3().setFromObject(inst.object);
        const size = box.getSize(new THREE.Vector3());
        const target = c.kind === "turret" ? 1.4 : 1.6;
        const scale = target / Math.max(size.x, size.y, size.z, 0.01);
        inst.object.scale.setScalar(scale);
        inst.object.rotation.y = modelForwardCorrection(c.defId);
        const centre = box.getCenter(new THREE.Vector3()).multiplyScalar(scale)
          .applyAxisAngle(new THREE.Vector3(0, 1, 0), inst.object.rotation.y);
        inst.object.position.set(-centre.x, -box.min.y * scale, -centre.z);
        // A turret's idle sweep must not rotate its barrel away from a firing target.
        actor.turret = inst.object.getObjectByName("turret") ?? null;
        actor.turretRest = actor.turret?.quaternion.clone() ?? null;
        inst.play("idle");
        if (this.reducedMotion) inst.mixer.update(0);
        group.add(inst.object);
        marker.visible = false;
        actor.instance = inst;
        actor.height = size.y * scale + 0.3;
        if (!actor.alive) {
          inst.play("death");
          group.visible = false;
        }
      })
      .catch(() => {
        if (!this.disposed && this.actors.get(c.id) === actor) this.onModelError();
      })
      .finally(() => {
        actor.loading = false;
      });
  }

  get ready(): boolean {
    return [...this.actors.values()].every((actor) => !actor.loading);
  }

  /** Adds a summoned zombie mid-battle. */
  summon(id: string, zombieId: string, side: Side, level: number): void {
    this.extras++;
    this.addActor(
      {
        id,
        kind: "zombie",
        defId: zombieId,
        slot: "extra",
        level,
        stars: 0,
        abilityLevels: [1],
        stats: {
          hp: 1,
          atk: 1,
          def: 0,
          intervalTicks: 10,
          crit: 0,
          critDmg: 1.5,
          acc: 100,
          eva: 0,
          energyPerAttack: 0,
          cargo: 0,
        },
        hpPct: 100,
      },
      side,
      this.extras,
    );
  }

  /** Applies one battle event to the actors (clips, beams, deaths, revives). */
  apply(e: BattleEvent, options: { silent?: boolean } = {}): void {
    const actor = e.a ? this.actors.get(e.a) : undefined;
    const target = e.b ? this.actors.get(e.b) : undefined;
    if (actor && target && actor !== target && actor.alive && ["target", "damage", "miss", "heal", "shield", "status"].includes(e.k) && !["reflect", "corrode", "explode"].includes(e.x ?? "")) {
      actor.aimTarget = target.id;
      actor.group.rotation.y = targetYaw(actor.group.position, target.group.position);
      if (actor.turret && actor.turretRest) actor.turret.quaternion.copy(actor.turretRest);
    }
    switch (e.k) {
      case "attack":
      case "ability":
        if (actor && !this.reducedMotion && !options.silent && (e.k === "attack" || (e.x && ["enemy", "enemies", "rear"].includes(archetypeById(`abl.${e.x}`).targets)))) {
          actor.instance?.play("attack");
          actor.idleAt = this.time + 0.7;
        }
        if (actor && isUltimate(e) && !options.silent) this.emit("ultimate", actor, actor, 0xf5c542);
        break;
      case "damage":
        if (actor && target && actor !== target && !options.silent) {
          const family = actor.defId.startsWith("unit.")
            ? unitById(actor.defId).chassis
            : actor.defId.startsWith("zmb.")
              ? "zombie"
              : "tank";
          audio.play(`hit.${family}`);
          this.emit(weaponFx(actor.defId, e.x), actor, target, e.x?.includes("!") ? 0xf5c542 : undefined);
        }
        if (target?.alive && !this.reducedMotion && !options.silent) {
          target.instance?.play("hit");
          target.idleAt = this.time + 0.3;
        }
        break;
      case "miss":
        if (actor && target && !options.silent) this.emit(weaponFx(actor.defId), actor, target, 0x8c9b9c);
        break;
      case "heal":
      case "shield":
      case "wall":
      case "status":
        if (target && !options.silent)
          this.emit(
            e.k === "status" ? statusFx(e) : e.k === "wall" ? "shield" : e.k,
            actor ?? target,
            target,
          );
        break;
      case "death":
        if (target) {
          target.alive = false;
          target.instance?.play("death");
          target.idleAt = null;
          target.hideAt = this.time + (this.reducedMotion || options.silent ? 0 : 1.6);
          if (this.reducedMotion || options.silent) target.group.visible = false;
        }
        break;
      case "revive":
        if (target) {
          target.alive = true;
          target.hideAt = null;
          target.group.visible = true;
          target.instance?.play("idle");
          if (!options.silent) this.emit("heal", actor ?? target, target);
        }
        break;
      case "summon":
        if (actor && e.b) this.summon(e.b, e.x ?? "zmb.shambler", actor.side, 10);
        break;
      default:
        break;
    }
  }

  private emit(kind: BattleFxKind, from: Actor, to: Actor, color?: number): void {
    const a = from.group.position.clone().setY(from.height * 0.6);
    const b = to.group.position.clone().setY(to.height * 0.6);
    if (from !== to && ["ballistic", "beam", "rocket", "pulse"].includes(kind)) {
      const forward = b.clone().sub(a).setY(0).normalize();
      a.addScaledVector(forward, 0.62);
    }
    this.fx.emit({ kind, from: a, to: b, ...(color === undefined ? {} : { color }) });
  }

  setReducedMotion(value: boolean): void {
    this.reducedMotion = value;
    this.fx.setReducedMotion(value);
    if (value)
      for (const actor of this.actors.values()) {
        actor.instance?.mixer.stopAllAction();
        actor.instance?.play("idle", { fade: 0 });
        actor.instance?.mixer.update(0);
        actor.idleAt = null;
        if (!actor.alive) actor.group.visible = false;
      }
  }

  setPlayback(speed: number, paused: boolean): void {
    this.playbackSpeed = speed;
    this.paused = paused;
  }

  settle(): void {
    this.fx.clear();
    for (const actor of this.actors.values()) {
      actor.hideAt = null;
      actor.idleAt = null;
      actor.group.visible = actor.alive;
      actor.instance?.play("idle", { fade: 0 });
    }
  }

  /** Resets every actor to its starting state (used by restart). */
  reset(input: BattleInput): void {
    this.load(input, this.visuals);
  }

  anchors(): Anchor[] {
    const out: Anchor[] = [];
    const rect = this.handle.renderer.domElement.getBoundingClientRect();
    const v = new THREE.Vector3();
    for (const a of this.actors.values()) {
      v.copy(a.group.position).add(this.root.position);
      v.y += a.height;
      v.project(this.handle.camera);
      out.push({
        id: a.id,
        x: ((v.x + 1) / 2) * rect.width,
        y: ((1 - v.y) / 2) * rect.height,
        visible: a.group.visible && Math.abs(v.x) < 1 && Math.abs(v.y) < 1 && v.z > -1 && v.z < 1,
      });
    }
    return out;
  }

  private tick(dt: number): void {
    if (this.savedCamera && this.aspect !== this.handle.camera.aspect) this.fitCamera();
    if (this.paused) return;
    dt *= this.playbackSpeed;
    this.time += dt;
    for (const a of this.actors.values()) {
      if (!this.reducedMotion) a.instance?.mixer.update(dt);
      if (a.alive && a.aimTarget && a.turret && a.turretRest) a.turret.quaternion.copy(a.turretRest);
      if (a.alive && a.idleAt !== null && this.time >= a.idleAt) {
        a.instance?.play("idle");
        a.idleAt = null;
      }
      if (a.hideAt !== null && this.time >= a.hideAt) {
        a.group.visible = false;
        a.hideAt = null;
      }
    }
    this.fx.update(dt);
  }

  private clear(): void {
    for (const a of this.actors.values()) {
      a.disposePaint?.();
      a.instance?.mixer.stopAllAction();
      if (a.instance) a.instance.mixer.uncacheRoot(a.instance.object);
      this.root.remove(a.group);
    }
    this.actors.clear();
    this.fx.clear();
    this.extras = 0;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.offFrame();
    this.clear();
    this.releaseCamera();
    this.fx.dispose();
    this.environment.dispose();
    this.floor.dispose();
    for (const geometry of this.localGeometry) geometry.dispose();
    for (const material of this.localMaterial) material.dispose();
    this.handle.scene.remove(this.root);
  }
}
